import axios from 'axios';
import {
  derivePinIdentity,
  deriveWrapKeyFromPin,
  fromBase64,
  PinDecryptError,
  unwrap,
  wipe,
} from './pinCrypto.helpers';
import { getCachedPinKey } from './pinKeyCache.helpers';

export type PinAttemptFailure = { status: 'wrong_pin' } | { status: 'locked_out'; retryAfterSeconds?: number };

/** Maps the expected PIN failures to a result; anything else (network, config) is left to the caller. */
export const toPinAttemptFailure = (error: unknown): PinAttemptFailure | undefined => {
  if (error instanceof PinDecryptError) return { status: 'wrong_pin' };

  if (axios.isAxiosError(error) && error.response?.status === 429) {
    const retryAfterSeconds = Number(error.response.headers?.['retry-after']);
    return {
      status: 'locked_out',
      retryAfterSeconds: Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : undefined,
    };
  }
};

/**
 * Derive the wrap key for the cached pin.key (one rate-limited pepper request) and decrypt the
 * refresh token inside it. Throws PinDecryptError for a wrong PIN.
 */
export const unwrapCachedRefreshToken = async (orgId: string, email: string, pin: string) => {
  const entry = getCachedPinKey(orgId, email);
  if (!entry || entry.pendingDelete) throw new Error('No PIN is set up for this user on this device');

  const salt = fromBase64(entry.tokenStore.salt);
  try {
    const pinIdentity = await derivePinIdentity(email, orgId);
    const key = await deriveWrapKeyFromPin(pinIdentity, pin, salt);
    const refreshToken = await unwrap(key, entry.tokenStore.iv, entry.tokenStore.ciphertext);
    return { entry, key, refreshToken };
  } finally {
    wipe(salt);
  }
};
